import React, { useEffect } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import * as ibantools from 'ibantools'
import axios from 'axios'

import { PartialDebitData, updateDebitData } from '../../store/paymentData'
import { RootState } from '../../store'
import { PaymentTypeProps } from './PaymentTypeSelector'
import { addToast } from '../../store/misc/misc'
import { ToastType } from '../../store/misc/interfaces'
import { useAppDispatch, useAppSelector } from '../../hooks'
import FormGroup from './FormGroup'
import { sanitizeInput } from '../../utils'

// Validation Schema for the debit data form
function getValidationSchema() {
  const fieldRequiredLabel = 'Dieses Feld ist zwingend erforderlich.'
  return yup.object().shape({
    payer_name: yup.string().required(fieldRequiredLabel),
    payer_street: yup.string()
      .matches(
        /^[a-zA-ZäöüÄÖÜß\s\d,.-]+\s([\d\s]+(?:\s?[-|+/]\s?\d+)?s*[a-z]?)$/,
        { message: 'Bitte die Adresse im Format "Straße Hausnummer" angeben.' },
      )
      .required(fieldRequiredLabel),
    payer_zip: yup
      .string()
      .matches(
        /^\d{5}$/,
        { message: 'Bitte eine valide Postleitzahl angeben.' },
      )
      .required(fieldRequiredLabel),
    payer_city: yup
      .string()
      .matches(
        /^[a-zA-ZäöüÄÖÜß\s-]+$/,
        { message: 'Bitte den Ort angeben.' },
      )
      .required(fieldRequiredLabel),
    payer_is_holder: yup.boolean(),
    holder_name: yup
      .string()
      .when(
        'payer_is_holder', {
        is: false,
        then: (schema) => schema.required(fieldRequiredLabel),
      }),
    holder_street: yup
      .string()
      .when(
        'payer_is_holder', {
        is: false,
        then: (schema) => schema.matches(
          /^[a-zA-ZäöüÄÖÜß\s\d,.-]+\s([\d\s]+(?:\s?[-|+/]\s?\d+)?s*[a-z]?)$/,
          { message: 'Bitte die Adresse im Format "Straße Hausnummer" angeben.' }).required(fieldRequiredLabel),
      }),
    holder_zip: yup
      .string()
      .when(
        'payer_is_holder', {
        is: false,
        then: (schema) => schema.matches(
          /^\d{5}$/,
          { message: 'Bitte eine valide Postleitzahl angeben.' }).required(fieldRequiredLabel),
      }),
    holder_city: yup
      .string()
      .when(
        'payer_is_holder', {
        is: false,
        then: (schema) => schema.matches(
          /^[a-zA-ZäöüÄÖÜß\s-]+$/,
          { message: 'Bitte den Ort angeben.' }).required(fieldRequiredLabel),
      }),
    iban: yup
      .string().required(fieldRequiredLabel)
      .test(
        'is-iban',
        'Bitte eine valide IBAN eingeben.',
        (value) => ibantools.isValidIBAN(value?.replace(/\s/g, '') || ''),
      ),
    bic: yup
      .string().required(fieldRequiredLabel)
      .test(
        'is-bic',
        'Bitte eine valide BIC eingeben.',
        ibantools.isValidBIC,
      ),
    acknowledged: yup.boolean().oneOf([true], fieldRequiredLabel),
  })
}

export const DebitFormReadOnly = () => {
  const { debitData } = useAppSelector((state: RootState) => state.paymentData)
  return <>
    <div className="card border-success">
      <div className="card-body">
        <h6 className="card-subtitle mb-3 text-muted">Die Lastschriftdaten der vorherigen Buchung werden automatisch übernommen.</h6>
        <table className="table table-striped">
          <tbody>
            <tr>
              <th scope="row">IBAN:</th>
              <td style={{wordBreak: "break-word"}}>{debitData.iban}</td>
            </tr>
            <tr>
              <th scope="row">BIC:</th>
              <td>{debitData.bic}</td>
            </tr>
            <tr>
              <th scope="row">Zahlungs<wbr/>pflichtiger:</th>
              <td>{debitData.payer_name}</td>
            </tr>
            <tr>
              <th scope="row">Adresse:</th>
              <td>{debitData.payer_address}</td>
            </tr>
            {debitData.payer_is_holder ?
              <>
                <tr>
                  <td colSpan={2}>
                    Die zahlungspflichtige Person stimmt mit dem Kontoinhaber überein.
                  </td>
                </tr>
              </> :
              <>
                <tr>
                  <th scope="row">Kontoinhaber:</th>
                  <td>{debitData.holder_name}</td>
                </tr>
                <tr>
                  <th scope="row">Adresse:</th>
                  <td>{debitData.holder_address}</td>
                </tr>
              </>
            }
          </tbody>
        </table>
      </div>
    </div>
  </>
}

export const DebitForm = (props: PaymentTypeProps) => {
  const dispatch = useAppDispatch()
  const { baseUrl } = useAppSelector((state: RootState) => state.misc)
  const { showReadOnly = false } = props;
  const { debitData } = useAppSelector((state: RootState) => state.paymentData)
  const { data: program } = useAppSelector((state: RootState) => state.program)
  const validationSchema = getValidationSchema()
  const {
    register,
    trigger,
    watch,
    formState: { errors },
    getValues,
    setValue,
    setError,
    formState,
  } = useForm<PartialDebitData>({
    resolver: yupResolver(validationSchema),
    // validate on first blur, then on every change: tabbing through an empty required field
    // marks it touched, so without this it would show a green check instead of an error
    mode: 'onTouched',
    defaultValues: debitData,
  })

  const watchPayerIsHolder = watch('payer_is_holder')
  const { addValidator, selected } = props

  // Handler to auto-fill BIC from IBAN via API
  const handleIbanChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const iban = e.target.value
    
    // Normalize IBAN (remove spaces)
    const normalizedIban = iban.replace(/\s/g, '')
    
    // Try to fetch BIC from API if IBAN is valid
    if (ibantools.isValidIBAN(normalizedIban)) {
      try {
        const response = await axios.get(`${baseUrl}/api/custom/validate/bankdata`, {
          params: { iban: normalizedIban }
        })
        if (response.data.bic && response.data.bic !== 'None') {
          setValue('bic', response.data.bic, { shouldValidate: true, shouldTouch: true })
        }
      } catch (err) {
        // Silently fail - user can enter BIC manually
        console.debug('Could not auto-fetch BIC for IBAN')
      }
    }
  }

  //TODO: this is not correct, don't use map, make types better
  const touchAll = () =>
    Object.entries(getValues()).forEach(([name, value]) => {
      // @ts-ignore
      return setValue(name, value)
    })

  // Stage sub-validator used to trigger the form validation from the
  // stage validation.
  const validator = async (): Promise<boolean> => {
    const valid = await trigger()

    if (valid) {
      const values = getValues()
      try {
        const data = (
          await axios.get(`${baseUrl}/api/custom/validate/bankdata`, {
            params: { 
              iban: values.iban?.replace(/\s/g, ''),
              bic: values.bic 
            },
          })
        ).data
        if (data.bic !== values.bic && data.bic !== values.bic + 'XXX') {
          setError('bic', {
            type: 'notMatch',
            message: 'Die BIC stimmt nicht mit der IBAN überein.',
          })
          setError('iban', {
            type: 'notMatch',
            message: 'Die IBAN stimmt nicht mit der BIC überein.',
          })
          dispatch(
            addToast({
              title: 'Fehler in IBAN oder BIC',
              body:
                'Die angegebene BIC und IBAN passen nicht zusammen.',
              type: ToastType.Danger,
            }),
          )
          return false
        }
      } catch (err: unknown) {
        dispatch(
          addToast({
            title: 'Server Probleme',
            body:
              'Die angegebene BIC und IBAN konnten nicht automatisch validiert werden.\ns/Bitte später noch einmal versuchen./s',
            type: ToastType.Warning,
          }),
        )
        return false // ensure that the data is correct
      }
      dispatch(updateDebitData(getValues()))
    } else {
      touchAll()
    }
    return valid
  }

  useEffect(
    () => {
      if (!showReadOnly) {
        addValidator({ trigger: validator, formName: 'DebitForm' })
      }
    },
    [addValidator, selected, showReadOnly, validator],
  )

  if (showReadOnly) {
    return <DebitFormReadOnly />;
  }

  let debitOrganizerText = ''
  if (program.debit_organizer_names.length !== 0) {
    debitOrganizerText = `sowie ${program.debit_organizer_names.length === 1 ? 'den' : 'die'
      } Veranstalter: ${program.debit_organizer_names.join(', ')}`
  }
  return (
    <Form id="debitForm">
      <Form.Row className="border-top border-bottom pt-3 mb-3">
        <Row>
          <Col>
            <h4>Daten des Zahlungspflichtigen</h4>
          </Col>
        </Row>
        <hr />
        <Row>
          <Col>
            <FormGroup
              controlId="payer_name"
              label="Name des Zahlungspflichtigen*"
              name="payer_name"
              type="text"
              autoComplete="name"
              register={register}
              formState={formState}
              errors={errors}
              setValue={setValue}
            />
            <Row>
              <Col md={12} sm={12}>
                <FormGroup
                  controlId="payer_street"
                  label="Straße & Hausnummer*"
                  name="payer_street"
                  type="text"
                  autoComplete="street-address"
                  register={register}
                  formState={formState}
                  errors={errors}
                  setValue={setValue}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6} sm={12}>
                <FormGroup
                  controlId="payer_zip"
                  label="Postleitzahl*"
                  name="payer_zip"
                  type="text"
                  autoComplete="postal-code"
                  register={register}
                  formState={formState}
                  errors={errors}
                  setValue={setValue}
                />
              </Col>
              <Col md={6} sm={12}>
                <FormGroup
                  controlId="payer_city"
                  label="Ort*"
                  name="payer_city"
                  type="text"
                  autoComplete="address-level2"
                  register={register}
                  formState={formState}
                  errors={errors}
                  setValue={setValue}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Form.Row>
      <Form.Row className="border-bottom mb-3">
        <Row>
          <Col>
            <h4>Daten des Kontoinhabers</h4>
          </Col>
        </Row>
        <Row>
          <Col className="my-2">
            <Form.Check
              {...register('payer_is_holder')}
              type="checkbox"
              label="Die zahlungspflichtige Person stimmt mit dem Kontoinhaber überein."
              id="payer_is_holder"
              aria-expanded={!watchPayerIsHolder}
              onClick={() => {
                setValue('payer_is_holder', !getValues('payer_is_holder'))
              }}
            />
          </Col>
        </Row>
        {!watchPayerIsHolder && (
          <Row>
            <Col>
              <FormGroup
                controlId="holder_name"
                label="Name des Kontoinhabers*"
                name="holder_name"
                type="text"
                autoComplete="name"
                register={register}
                formState={formState}
                errors={errors}
                setValue={setValue}
              />
              <Row>
                <Col md={12} sm={12}>
                  <FormGroup
                    controlId="holder_street"
                    label="Straße &amp; Hausnummer*"
                    name="holder_street"
                    type="text"
                    autoComplete="street-address"
                    register={register}
                    formState={formState}
                    errors={errors}
                    setValue={setValue}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6} sm={12}>
                  <FormGroup
                    controlId="holder_zip"
                    label="Postleitzahl*"
                    name="holder_zip"
                    type="text"
                    autoComplete="postal-code"
                    register={register}
                    formState={formState}
                    errors={errors}
                    setValue={setValue}
                  />
                </Col>
                <Col md={6} sm={12}>
                  <FormGroup
                    controlId="holder_city"
                    label="Ort*"
                    name="holder_city"
                    type="text"
                    autoComplete="address-level2"
                    register={register}
                    formState={formState}
                    errors={errors}
                    setValue={setValue}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </Form.Row>
      <Form.Row className="border-bottom mb-3">
        <Row>
          <Col>
            <h4>Kontodaten</h4>
          </Col>
        </Row>
        <Row>
          <Col md={6} sm={12}>
            <Form.Group controlId="iban">
              <Form.Label>IBAN*</Form.Label>
              <Form.Control
                {...register('iban', {
                  setValueAs: (value) => sanitizeInput(value),
                  onChange: handleIbanChange,
                })}
                type="text"
                isValid={formState.touchedFields.iban && !errors.iban}
                isInvalid={formState.touchedFields.iban && !!errors.iban}
              />
              <Form.Control.Feedback type="invalid">
                {errors.iban && errors.iban.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group controlId="bic">
              <Form.Label>BIC*</Form.Label>
              <Form.Control
                {...register('bic', {
                  setValueAs: (value) => sanitizeInput(value)
                })}
                type='text'
                isValid={formState.touchedFields.bic && !errors.bic}
                isInvalid={formState.touchedFields.bic && !!errors.bic}
              />
              <Form.Control.Feedback type="invalid">
                {errors.bic && errors.bic.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <small className="text-muted">
              IBAN und BIC befinden sich auf Kontoauszügen, der Bankkarte oder
              der Webseite der Bank.
            </small>
          </Col>
        </Row>
      </Form.Row>
      <Form.Row className="border-bottom mb-3">
        <Card text="white">
          <Card.Body>
            <Form.Check
              {...register('acknowledged')}
              type="checkbox"
              id="acknowledged"
              isValid={
                formState.touchedFields.acknowledged && !errors.acknowledged
              }
              isInvalid={
                formState.touchedFields.acknowledged && !!errors.acknowledged
              }
              className={
                !formState.touchedFields.acknowledged ? 'text-dark' : undefined
              }
              label={`Ich ermächtige ${program.debit_creditor_name}${
                debitOrganizerText ? ', ' + debitOrganizerText : ','
              } Zahlungen von meinem Konto
                  per Lastschrift einzuziehen. Zugleich weise ich mein
                  Kreditinstitut an, die von ${debitOrganizerText ? 'den oben genannten Institutionen' : 'der oben genannten Institution'} auf mein Konto
                  gezogenen Lastschriften einzulösen. Hinweis: Ich kann innerhalb
                  von acht Wochen, beginnend mit dem Belastungsdatum, die
                  Erstattung der per Lastschrift eingezogenen Zahlung verlangen.
                  Es gelten dabei die mit meinem Kreditinstitut vereinbarten
                  Bedingungen.`}
              feedback={
                errors.acknowledged ? errors.acknowledged.message : undefined
              }
            />
          </Card.Body>
        </Card>
      </Form.Row>
    </Form>
  )
}
